import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import Layout from '@/components/Layout';
import { getLocalBusinessSchema, getServiceSchema } from '@/utils/seoSchemas';

export default function Home() {
  const navigate = useNavigate();

  const schemas = [
    getLocalBusinessSchema(),
    getServiceSchema()
  ];

  return (
    <Layout>
      <SEOHead
        title="Conciergerie Airbnb Suisse Romande - Gestion Complète Location Courte Durée | Homely"
        description="Conciergerie Airbnb professionnelle en Suisse romande. +35% revenus, 0h investis, 4.9★ moyenne. Gestion complète 24/7 : Lausanne, Genève, Verbier, Montreux. Estimation gratuite en 2 min."
        canonical="https://homely-conciergerie.ch/"
        ogImage="https://images.pexels.com/photos/38289184/pexels-photo-38289184.jpeg?auto=compress&cs=tinysrgb&w=1200"
        schemas={schemas}
      />
      <div className="min-h-screen bg-[#F5F3EF]">
      {/* Hero Section with Background Image */}
      <div 
        className="relative bg-cover bg-center py-32 px-6"
        style={{
          backgroundImage: `linear-gradient(rgba(43, 43, 43, 0.6), rgba(43, 43, 43, 0.75)), url('https://images.pexels.com/photos/38289184/pexels-photo-38289184.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h1 
            className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight"
            data-testid="home-hero-title"
          >
            Conciergerie Airbnb<br />en Suisse romande
          </h1>
          <p className="text-2xl md:text-3xl text-gray-100 mb-8 leading-relaxed max-w-2xl mx-auto font-light">
            Gestion complète, revenus optimisés, zéro contrainte
          </p>
          <Button 
            onClick={() => navigate('/estimation')}
            className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold rounded-lg transition-all transform hover:-translate-y-1 shadow-lg"
            data-testid="home-cta-estimation"
          >
            Estimer mon bien <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-20">

        {/* Le vrai coût d'un Airbnb mal géré */}
        <div className="mt-20 max-w-4xl mx-auto">
          <div className="bg-white p-12 rounded-2xl shadow-sm border-l-4 border-[#8A9474]">
            <h2 className="text-4xl font-bold text-[#2B2B2B] mb-6">
              Vous perdez en moyenne 40% de revenus potentiels
            </h2>
            <p className="text-xl text-[#4A4A4A] mb-6 leading-relaxed">
              Prix fixes qui ignorent la haute saison. Calendrier vide 30% de l'année. 
              Mauvaises reviews à cause d'un check-in raté. Messages à 23h un samedi soir.
            </p>
            <p className="text-2xl font-semibold text-[#2B2B2B]">
              Votre bien ne devrait pas être un second job.
            </p>
          </div>
        </div>

        {/* La solution Homely */}
        <div className="mt-16 max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-[#2B2B2B] mb-4">
              On transforme votre bien en machine à revenus
            </h2>
            <p className="text-2xl text-[#6B6B6B]">
              Sans que vous leviez le petit doigt
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-[#8A9474]/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-[#8A9474]">+35%</span>
              </div>
              <h3 className="text-2xl font-bold text-[#2B2B2B] mb-3">Revenus augmentés</h3>
              <p className="text-lg text-[#6B6B6B]">
                Pricing dynamique intelligent qui suit la demande réelle. Fini les prix au doigt mouillé.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-[#8A9474]/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-[#8A9474]">0h</span>
              </div>
              <h3 className="text-2xl font-bold text-[#2B2B2B] mb-3">Temps investi</h3>
              <p className="text-lg text-[#6B6B6B]">
                Zéro message. Zéro ménage. Zéro prise de tête. Nous gérons absolument tout, 24/7.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-[#8A9474]/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-[#8A9474]">4.9</span>
              </div>
              <h3 className="text-2xl font-bold text-[#2B2B2B] mb-3">Note moyenne</h3>
              <p className="text-lg text-[#6B6B6B]">
                Accueil pro, logement impeccable, réactivité immédiate. Vos reviews grimpent, vos réservations aussi.
              </p>
            </div>
          </div>
        </div>

        {/* Social Proof réécrit */}
        <div className="mt-20 max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-[#E8EBE4] to-[#F5F3EF] p-12 rounded-2xl">
            <h3 className="text-4xl font-bold text-[#2B2B2B] mb-3 text-center">
              Ils ont arrêté de perdre leur temps
            </h3>
            <p className="text-center text-xl text-[#6B6B6B] mb-10">Et leurs revenus ont explosé</p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-sm">
                <div className="flex items-center gap-1 mb-4">
                  <span className="text-yellow-500">★★★★★</span>
                </div>
                <p className="text-lg text-[#4A4A4A] mb-4 leading-relaxed">
                  "Mon chalet à Verbier me rapportait 45'000 CHF par an, en me bouffant tous mes week-ends. 
                  Aujourd'hui : 68'000 CHF, et je ne touche plus à rien."
                </p>
                <p className="text-sm font-semibold text-[#2B2B2B]">Pierre M., propriétaire à Verbier</p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm">
                <div className="flex items-center gap-1 mb-4">
                  <span className="text-yellow-500">★★★★★</span>
                </div>
                <p className="text-lg text-[#4A4A4A] mb-4 leading-relaxed">
                  "J'ai arrêté de répondre aux messages à minuit. Mon taux d'occupation est passé de 52% à 87%. 
                  La différence ? Homely."
                </p>
                <p className="text-sm font-semibold text-[#2B2B2B]">Sophie L., propriétaire à Lausanne</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Final */}
        <div className="mt-20 max-w-3xl mx-auto text-center bg-[#2B2B2B] p-12 rounded-2xl">
          <h2 className="text-4xl font-bold text-white mb-4">
            Combien pourriez-vous gagner en plus ?
          </h2>
          <p className="text-2xl text-gray-300 mb-8">
            Estimation gratuite en 2 minutes. Sans engagement.
          </p>
          <Button 
            onClick={() => navigate('/estimation')}
            size="lg"
            className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-6 sm:px-10 py-4 sm:py-7 text-base sm:text-lg font-semibold whitespace-normal sm:whitespace-nowrap"
          >
            Obtenir mon estimation gratuite
          </Button>
          
          {/* Liens internes villes principales */}
          <div className="mt-10 pt-10 border-t border-gray-700">
            <p className="text-gray-400 mb-4">Découvrez nos services de conciergerie Airbnb par ville :</p>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { name: 'Lausanne', slug: 'lausanne' },
                { name: 'Genève', slug: 'geneve' },
                { name: 'Verbier', slug: 'verbier' },
                { name: 'Montreux', slug: 'montreux' },
                { name: 'Crans-Montana', slug: 'crans-montana' },
                { name: 'Neuchâtel', slug: 'neuchatel' },
                { name: 'Vevey', slug: 'vevey' },
                { name: 'Fribourg', slug: 'fribourg' }
              ].map((ville) => (
                <button
                  key={ville.slug}
                  onClick={() => navigate(`/gestion-locative/${ville.slug}`)}
                  className="text-[#8A9474] hover:text-white text-sm underline"
                >
                  {ville.name}
                </button>
              ))}
            </div>
            <div className="mt-6">
              <button
                onClick={() => navigate('/nos-villes')}
                className="text-white hover:text-[#8A9474] font-semibold"
              >
                Voir toutes nos villes →
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </Layout>
  );
}
